import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import deleteRequisitionLine from "@insite/client-framework/Store/Pages/Requisitions/Handlers/DeleteRequisitionLine";
import updateRequisitionLine from "@insite/client-framework/Store/Pages/Requisitions/Handlers/UpdateRequisitionLine";
import translate from "@insite/client-framework/Translate";
import { RequisitionLineModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import { IconMemo, IconPresentationProps } from "@insite/mobius/Icon";
import Trash2 from "@insite/mobius/Icons/Trash2";
import Link from "@insite/mobius/Link";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    requisitionId: string;
    requisitionLine: RequisitionLineModel;
}

const mapStateToProps = (state: ApplicationState) => ({
    isEditing: state.pages.requisitions.isEditing,
});

const mapDispatchToProps = {
    updateRequisitionLine,
    deleteRequisitionLine,
};

type Props = OwnProps &
    HasToasterContext &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps>;

export interface RequisitionsRequisitionLineRowMobileStyles {
    row?: DataTableRowProps;
    qtyOrderedCell?: DataTableCellProps;
    qtyOrderedTextField?: TextFieldPresentationProps;
    dateCell?: DataTableCellProps;
    costCodeCell?: DataTableCellProps;
    firstNameCell?: DataTableCellProps;
    lastNameCell?: DataTableCellProps;
    usernameCell?: DataTableCellProps;
    removeCell?: DataTableCellProps;
    removeCartLineIcon?: IconPresentationProps;
    requisitionMobileStyle?: InjectableCss;
    hiddenWidth?: InjectableCss;
}

export const requisitionsRequisitionLineRowStyles: RequisitionsRequisitionLineRowMobileStyles = {
    qtyOrderedTextField: {
        cssOverrides: {
            formField: css`
                width: 70px;
                input::-webkit-outer-spin-button,
                input::-webkit-inner-spin-button {
                    -webkit-appearance: none;
                    margin: 0;
                }

                /* Firefox */
                input[type="number"] {
                    -moz-appearance: textfield;
                }

                input[type="number"] {
                    appearance: none;
                    -webkit-appearance: none;
                    -moz-appearance: none;
                }
            `,
        },
    },
    removeCartLineIcon: {
        src: Trash2,
        color: "#ef3742",
        css: css`
            width: 20px;
            height: 20px;
            &:hover {
                opacity: 0.7;
            }
        `,
    },
    requisitionMobileStyle: {
        css: css`
            [class^="GridItemStyle"] {
                padding: 5px 0;
            }
            [class^="GridOffset"] {
                margin: 0;
                width: 100% !important;
            }
            padding: 15px;
            h4 {
                font-weight: bold;
            }
            &:nth-child(odd) {
                background: #f3f3f3;
            }
        `,
    },
};

const styles = requisitionsRequisitionLineRowStyles;

const RequisitionsRequisitionLineRowMobile = ({
    requisitionId,
    requisitionLine,
    toaster,
    updateRequisitionLine,
    deleteRequisitionLine,
    isEditing,
}: Props) => {
    const qtyOrderedSubmitHandler = (value: string) => {
        const inputQty = Number.parseInt(value, 10);
        if (Number.isNaN(inputQty) || requisitionLine.qtyOrdered === inputQty) {
            return;
        }

        updateRequisitionLine({
            requisitionId,
            requisitionLineId: requisitionLine.id,
            requisitionLine: {
                ...requisitionLine,
                qtyOrdered: inputQty,
            },
            onSuccess: () => {
                toaster.addToast({ body: siteMessage("Requisition_ItemUpdated"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.result) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const {
        value: qtyOrdered,
        changeHandler: qtyOrderedChangeHandler,
        keyDownHandler: qtyOrderedKeyDownHandler,
        blurHandler: qtyOrderedBlurHandler,
    } = useAccessibleSubmit(requisitionLine.qtyOrdered!.toString(), qtyOrderedSubmitHandler);

    const removeLineClickHandler = () => {
        deleteRequisitionLine({
            requisitionId,
            requisitionLineId: requisitionLine.id,
            onSuccess: () => {
                toaster.addToast({ body: siteMessage("Requisition_ItemDeleted"), messageType: "success" });
            },
            onComplete() {
                // "this" is targeting the object being created, not the parent SFC
                // eslint-disable-next-line react/no-this-in-sfc
                this.onSuccess?.();
            },
        });
    };

    return (
        <>
            <Hidden above="md" data-test-key={requisitionLine.id}>
                <StyledWrapper {...styles.requisitionMobileStyle} data-test-selector="requisitionLine">
                    <GridContainer>
                        <GridItem width={5}>
                            <Typography as="h4">{translate("Qty")}</Typography>
                        </GridItem>
                        <GridItem width={3}>
                            <TextField
                                type="number"
                                min={1}
                                value={qtyOrdered}
                                disabled={isEditing}
                                onChange={qtyOrderedChangeHandler}
                                onKeyDown={qtyOrderedKeyDownHandler}
                                onBlur={qtyOrderedBlurHandler}
                            />
                        </GridItem>
                    </GridContainer>
                    <GridContainer>
                        <GridItem width={5}>
                            <Typography as="h4">{translate("Date")}</Typography>
                        </GridItem>
                        <GridItem width={7}>
                            <LocalizedDateTime dateTime={requisitionLine.orderDate} />
                        </GridItem>
                    </GridContainer>
                    <GridContainer>
                        <GridItem width={5}>
                            <Typography as="h4">{translate("First Name")}</Typography>
                        </GridItem>
                        <GridItem width={7}> {requisitionLine.firstName}</GridItem>
                    </GridContainer>
                    <GridContainer>
                        <GridItem width={5}>
                            <Typography as="h4">{translate("Last Name")}</Typography>
                        </GridItem>
                        <GridItem width={7}>{requisitionLine.lastName}</GridItem>
                    </GridContainer>
                    <GridContainer>
                        <GridItem width={5}>
                            <Typography as="h4">{translate("Username")}</Typography>
                        </GridItem>
                        <GridItem width={7}>{requisitionLine.userName}</GridItem>
                    </GridContainer>
                    <GridContainer>
                        <GridItem width={5}></GridItem>
                        <GridItem width={7}>
                            {" "}
                            <Link onClick={removeLineClickHandler} disabled={isEditing} data-test-selector="delete">
                                <IconMemo {...styles.removeCartLineIcon} />
                            </Link>
                        </GridItem>
                    </GridContainer>
                </StyledWrapper>
            </Hidden>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(RequisitionsRequisitionLineRowMobile));
